import padStart from 'lodash/padStart';
import * as Sentry from '@sentry/browser';
import { LogglyTracker } from 'loggly-jslogger';
import { getUserFullName } from './../utils/utilsChat';
import { showAlert } from './../components';


export default class Logger {

  isEnabled = false;
  isLogglyEnabled = false;
  isSentryEnabled = false;

  logglyKey = null;
  sentryKey = null;

  scopeUser = {};
  scopeData = {
    hostname: location.hostname,
    version: process.env.ELIXIRCHAT_VERSION,
  };

  constructor({ loggly, sentry }){
    try {
      this.isEnabled = JSON.parse(process.env.DEBUG);
    }
    catch (e) {}

    this.logglyKey = process.env.LOGGLY_TOKEN;
    this.sentryKey = process.env.SENTRY_URL;

    this.isLogglyEnabled = loggly;
    this.isSentryEnabled = sentry;
    this.initializeLoggers();
  }

  initializeLoggers = () => {
    if (this.logglyKey && this.isLogglyEnabled) {
      this.logglyLogger = new LogglyTracker();
      this.logglyLogger.push({ logglyKey: this.logglyKey });
    }

    if (this.sentryKey && this.isSentryEnabled) {
      Sentry.init({ dsn: this.sentryKey });
      Sentry.configureScope(scope => {
        scope.setExtra('hostname', location.hostname);
      });
    }
  };

  setUser = (user) => {
    this.scopeUser = user;
    Sentry.configureScope(scope => {
      scope.setUser({
        id: user.id,
        email: user.email,
        username: getUserFullName(user),
      });
    });
  };

  sendErrorToSentry = (message, data) => {

    // TODO: squeeze large data before sending
    // TODO: send in chunks on interval, not immediately (use window.requestIdleCallback()?)

    if (!this.isSentryEnabled || !this.sentryKey) {
      return;
    }
    Sentry.withScope(scope => {
      scope.setExtras(data);
      Sentry.captureMessage(message);
    });
  };

  sendEventToLoggly = (message, data, type) => {

    // TODO: squeeze large data before sending
    // TODO: send in chunks on interval, not immediately (use window.requestIdleCallback()?)

    if (!this.isLogglyEnabled || !this.logglyKey) {
      return;
    }
    this.logglyLogger.push({
      message,
      type,
      data: data || 'none',
      useUtfEncoding: true,
      sendConsoleErrors: true,
      userEmail: this.scopeUser.email || 'unknown-email',
      ...this.scopeData,
    });
  };

  getTimestamp = () => {
    const now = new Date();
    return [
      padStart(now.getHours().toString(), 2, '0'),
      padStart(now.getMinutes().toString(), 2, '0'),
      padStart(now.getSeconds().toString(), 2, '0'),
    ].join(':');
  };

  logInfo = (text, data) => {
    if (!this.isEnabled) {
      return;
    }
    this.sendEventToLoggly(text, data, 'info');
    this.writeConsoleMessage(text, data);
  };

  logError = (text, data) => {
    if (!this.isEnabled) {
      return;
    }
    this.sendErrorToSentry(text, data);
    this.sendEventToLoggly(text, data, 'error');
    this.writeConsoleMessage(text, data, '#EB3223');
  };

  logEvent = (text, data) => {
    if (!this.isEnabled) {
      return;
    }
    const isErrorEvent = /_ERROR$/i.test(text);
    const eventText = data.firedOnce ? text + ' (FIRED ONCE)' : text;
    if (isErrorEvent) {
      this.sendErrorToSentry(eventText, data);
    }
    this.sendEventToLoggly(eventText, data, 'event');
    this.writeConsoleMessage(eventText, data, isErrorEvent ? '#EBA4A7' : '#5EE9EB');
  };

  writeConsoleMessage = (message, data, color = '') => {
    const messageConsoleStyle = `font-weight: bold; color: ${color};`;
    const infoButtonConsoleStyle = `font-weight: normal; text-decoration: underline; color: ${color};`;
    const arrowConsoleStyle = `font: 10px Arial; padding-left: 3px; color: ${color};`;
    const additionalDataConsoleStyle = `font-weight: bold;`;

    console.groupCollapsed(`%c${this.getTimestamp()} ${message} %cInfo%c▾`, messageConsoleStyle, infoButtonConsoleStyle, arrowConsoleStyle);

    if (data && typeof data === 'object' && !(data instanceof Array)) {
      for (let key in data) {
        console.log(`%c${key}:\n`, additionalDataConsoleStyle, data[key], '\n');
      }
    }
    else {
      console.log('%c\nData:\n', additionalDataConsoleStyle, data);
    }
    console.log('%c\nStacktrace:', additionalDataConsoleStyle);
    console.trace();
    console.groupEnd();
  };
}
